// Lazy Load Color Swatches for Product Cards
// Loads additional color swatches asynchronously after page load to improve performance
// First searches products already on the page, then uses search API as fallback

class ProductCardSwatchLoader {
  constructor() {
    this.loadedSwatches = new Set(); // Track which swatch containers have been loaded
    this.maxSwatches = 25;
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.startLoading());
    } else {
      this.startLoading();
    }
  }

  startLoading() {
    // Load product page swatches immediately (they're always visible)
    const productPageSwatches = document.querySelectorAll(
      '.product-page__color-swatches[data-lazy-load-swatches="true"]'
    );
    if (productPageSwatches.length > 0) {
      productPageSwatches.forEach((container) => {
        const containerId = container.dataset.currentProductId;
        if (containerId) {
          this.loadSwatchesForContainer(container);
        }
      });
    }

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.loadSwatches(), { timeout: 2000 });
    } else {
      setTimeout(() => this.loadSwatches(), 1000);
    }

    // Setup Intersection Observer for cards that come into view
    this.setupIntersectionObserver();

    // Setup Mutation Observer to watch for new product cards (infinite scroll)
    this.setupMutationObserver();

    // Listen for product swap events to reload swatches
    this.setupProductSwapListener();
  }

  setupProductSwapListener() {
    // Listen for product-info loaded event (fires after product swap)
    document.addEventListener(
      'product-info:loaded',
      (e) => {
        // Wait a bit for DOM to settle
        setTimeout(() => {
          const productPageSwatches = document.querySelectorAll(
            '.product-page__color-swatches[data-lazy-load-swatches="true"]'
          );
          productPageSwatches.forEach((container) => {
            const containerId = container.dataset.currentProductId;
            if (containerId) {
              // Reset the loaded state for this container to allow reloading
              const containerKey = `${containerId}-${
                container.dataset.baseTitle || ''
              }`;
              this.loadedSwatches.delete(containerKey);
              // Reload swatches for the new product
              this.loadSwatchesForContainer(container);
            }
          });
        }, 100);
      },
      false
    );
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: load all swatches after delay
      setTimeout(() => this.loadSwatches(), 2000);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const container = entry.target;
            const containerId = container.dataset.currentProductId;
            if (containerId && !this.loadedSwatches.has(containerId)) {
              this.loadSwatchesForContainer(container);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading slightly before card is visible
        threshold: 0.1,
      }
    );

    // Observe all existing swatch containers
    this.observeContainers();
  }

  setupMutationObserver() {
    if (!('MutationObserver' in window)) {
      // Fallback: periodically check for new containers
      setInterval(() => this.checkForNewContainers(), 2000);
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      let hasNewContainers = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is a product card, product page swatch container, or contains them
          if (node.nodeType === 1) {
            // Check if node itself is a product card or swatch container
            if (
              node.matches &&
              (node.matches('product-card') ||
                node.matches('[data-lazy-load-swatches="true"]') ||
                node.matches('.product-page__color-swatches'))
            ) {
              hasNewContainers = true;
            }
            // Check if node contains product cards or swatch containers
            const productCards = node.querySelectorAll
              ? node.querySelectorAll(
                  'product-card, [data-lazy-load-swatches="true"]'
                )
              : [];
            if (productCards.length > 0) {
              hasNewContainers = true;
            }
          }
        });
      });

      if (hasNewContainers) {
        // Use requestIdleCallback or small delay to batch DOM queries
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            this.observeContainers();
            this.loadSwatches();
          });
        } else {
          setTimeout(() => {
            this.observeContainers();
            this.loadSwatches();
          }, 100);
        }
      }
    });

    // Observe the document body for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  observeContainers() {
    if (!this.intersectionObserver) {
      return;
    }

    // Find all swatch containers that aren't being observed yet
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    containers.forEach((container) => {
      const containerId = container.dataset.currentProductId;
      const containerKey = `${containerId}-${
        container.dataset.baseTitle || ''
      }`;
      // Only observe if not already loaded and has valid ID
      if (containerId && !this.loadedSwatches.has(containerKey)) {
        this.intersectionObserver.observe(container);
      }
    });
  }

  checkForNewContainers() {
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );
    let hasNew = false;

    containers.forEach((container) => {
      const containerId = container.dataset.currentProductId;
      const containerKey = `${containerId}-${
        container.dataset.baseTitle || ''
      }`;
      if (containerId && !this.loadedSwatches.has(containerKey)) {
        hasNew = true;
        this.observeContainers();
      }
    });

    if (hasNew) {
      this.loadSwatches();
    }
  }

  async loadSwatches() {
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    // Load swatches in batches to avoid overwhelming the browser
    const batchSize = 10;
    for (let i = 0; i < containers.length; i += batchSize) {
      const batch = Array.from(containers).slice(i, i + batchSize);
      await Promise.all(
        batch.map((container) => this.loadSwatchesForContainer(container))
      );

      // Small delay between batches
      if (i + batchSize < containers.length) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
  }

  async loadSwatchesForContainer(container) {
    const containerId = container.dataset.currentProductId;
    if (!containerId) {
      return;
    }

    const baseTitle = container.dataset.baseTitle;
    if (!baseTitle) {
      return;
    }

    // Check if this container was already loaded (prevent duplicate loading)
    // But allow reloading if the container was replaced (product swap)
    const containerKey = `${containerId}-${baseTitle}`;
    if (this.loadedSwatches.has(containerKey)) {
      // Check if container still has the same structure (not replaced)
      const existingSwatches = container.querySelectorAll(
        '.product-card__color-swatch, .product-page__color-swatch'
      );
      // If there's only 1 swatch (the current product), it might need reloading
      if (existingSwatches.length > 1) {
        return; // Already loaded and has multiple swatches
      }
    }

    this.loadedSwatches.add(containerKey);

    // Clear ALL existing swatches except the first one (current product)
    this.clearExistingSwatches(container);

    // Get the current product's swatch to ensure we don't include it
    const currentSwatch = container.querySelector(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const currentProductId = currentSwatch?.dataset.productId || containerId;

    // Use a Set to track all product IDs we've found (ensures uniqueness)
    const foundProductIds = new Set([currentProductId]);
    const swatches = [];

    // First, try to find swatches from other containers on the page
    // But ONLY get the FIRST swatch from each container (represents that product)
    const allContainers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    for (const otherContainer of allContainers) {
      if (swatches.length >= this.maxSwatches) {
        break;
      }

      // Skip current container
      if (otherContainer === container) {
        continue;
      }

      // Must have matching base title
      const otherBaseTitle = otherContainer.dataset.baseTitle;
      if (!otherBaseTitle || otherBaseTitle !== baseTitle) {
        continue;
      }

      // Get ONLY the first swatch from this container (the product's own swatch)
      const firstSwatch =
        otherContainer.querySelector(
          '.product-card__color-swatch:first-child, .product-page__color-swatch:first-child'
        ) ||
        otherContainer.querySelector(
          '.product-card__color-swatch, .product-page__color-swatch'
        );

      if (!firstSwatch) {
        continue;
      }

      const productId = firstSwatch.dataset.productId;
      if (!productId || foundProductIds.has(productId)) {
        continue;
      }

      foundProductIds.add(productId);

      swatches.push({
        color: firstSwatch.dataset.color,
        productId: firstSwatch.dataset.productId,
        productHandle: firstSwatch.dataset.productHandle,
        productUrl: firstSwatch.dataset.productUrl,
        backgroundColor: firstSwatch.style.backgroundColor,
        isUnavailable:
          firstSwatch.classList.contains(
            'product-card__color-swatch--unavailable'
          ) ||
          firstSwatch.classList.contains(
            'product-page__color-swatch--unavailable'
          ),
        isProductPage: firstSwatch.classList.contains(
          'product-page__color-swatch'
        ),
      });
    }

    // First, check if we have matching products data from Liquid (product cards only - current collection)
    const matchingProductsData = container.dataset.matchingProducts;
    if (matchingProductsData) {
      try {
        const matchingProducts = JSON.parse(matchingProductsData);
        matchingProducts.forEach((product) => {
          if (
            swatches.length < this.maxSwatches &&
            product.id &&
            product.id !== currentProductId &&
            !foundProductIds.has(product.id)
          ) {
            foundProductIds.add(product.id);
            swatches.push({
              color: product.color || '',
              productId: product.id,
              productHandle: product.handle,
              productUrl: product.url,
              backgroundColor: product.swatch || '',
              isUnavailable: !product.available || product.unavailable,
              isProductPage: container.classList.contains(
                'product-page__color-swatches'
              ),
            });
          }
        });
      } catch (e) {
        console.debug('Error parsing matching products data:', e);
      }
    }

    // If we need more swatches, use search API (more efficient - single request)
    if (swatches.length < this.maxSwatches) {
      const apiSwatches = await this.findSwatchesViaSearchAPI(
        baseTitle,
        currentProductId,
        foundProductIds
      );

      // Add API swatches, ensuring no duplicates
      for (const apiSwatch of apiSwatches) {
        if (swatches.length >= this.maxSwatches) {
          break;
        }
        if (
          apiSwatch.productId &&
          !foundProductIds.has(apiSwatch.productId) &&
          apiSwatch.productId !== currentProductId
        ) {
          foundProductIds.add(apiSwatch.productId);
          swatches.push(apiSwatch);
        }
      }
    }

    // Before rendering, check what swatches already exist in the container
    // and filter out any duplicates
    const existingSwatches = container.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const existingProductIds = new Set();
    existingSwatches.forEach((swatch) => {
      const productId = swatch.dataset.productId;
      if (productId) {
        existingProductIds.add(productId);
      }
    });

    // Filter out swatches that already exist in the container
    const uniqueSwatches = swatches.filter(
      (swatch) => !existingProductIds.has(swatch.productId)
    );

    // Render swatches (only if we have any new ones)
    if (uniqueSwatches.length > 0) {
      this.renderSwatches(container, uniqueSwatches);
    }
  }

  clearExistingSwatches(container) {
    const isProductPage = container.classList.contains(
      'product-page__color-swatches'
    );
    const wrapper = isProductPage
      ? container
      : container.querySelector('.swiper-wrapper');

    if (!wrapper) {
      return;
    }

    // Keep the first swatch (current product), remove the rest
    const allSwatches = wrapper.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );

    if (isProductPage) {
      // For product pages, remove all except the first
      allSwatches.forEach((swatch, index) => {
        if (index > 0) {
          swatch.remove();
        }
      });
    } else {
      // For product cards, remove slides except the first
      const slides = wrapper.querySelectorAll('.swiper-slide');
      slides.forEach((slide, index) => {
        if (index > 0) {
          slide.remove();
        }
      });
    }
  }

  findSwatchesOnPage(container, baseTitle, currentProductId) {
    const swatches = [];
    const foundProductIds = new Set([currentProductId]); // Always exclude current product
    const isProductPage = container.classList.contains(
      'product-page__color-swatches'
    );

    // Get all swatches from the current container to exclude them
    const currentSwatches = container.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const currentSwatchProductIds = new Set();
    currentSwatches.forEach((swatch) => {
      const productId = swatch.dataset.productId;
      if (productId) {
        currentSwatchProductIds.add(productId);
      }
    });

    // Find all swatch containers on the page (both product cards and product pages)
    const allSwatchContainers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    allSwatchContainers.forEach((swatchContainer) => {
      if (swatches.length >= this.maxSwatches) {
        return;
      }

      // Skip the current container
      if (swatchContainer === container) {
        return;
      }

      const containerBaseTitle = swatchContainer.dataset.baseTitle;
      // Must match base title exactly
      if (!containerBaseTitle || containerBaseTitle !== baseTitle) {
        return;
      }

      // Get the FIRST swatch from this container (represents that product)
      // This ensures we only get one swatch per product
      let existingSwatch = swatchContainer.querySelector(
        '.product-card__color-swatch:first-of-type, .product-page__color-swatch:first-of-type'
      );

      // Fallback if :first-of-type doesn't work
      if (!existingSwatch) {
        const cardSwatch = swatchContainer.querySelector(
          '.product-card__color-swatch'
        );
        const pageSwatch = swatchContainer.querySelector(
          '.product-page__color-swatch'
        );
        existingSwatch = cardSwatch || pageSwatch;
      }

      if (!existingSwatch) {
        return;
      }

      const productId = existingSwatch.dataset.productId;
      // Skip if:
      // - No product ID
      // - Already found (duplicate)
      // - Is the current product
      // - Already exists in current container
      if (
        !productId ||
        foundProductIds.has(productId) ||
        productId === currentProductId ||
        currentSwatchProductIds.has(productId)
      ) {
        return;
      }

      foundProductIds.add(productId);

      const swatchData = {
        color: existingSwatch.dataset.color,
        productId: existingSwatch.dataset.productId,
        productHandle: existingSwatch.dataset.productHandle,
        productUrl: existingSwatch.dataset.productUrl,
        backgroundColor: existingSwatch.style.backgroundColor,
        isUnavailable:
          existingSwatch.classList.contains(
            'product-card__color-swatch--unavailable'
          ) ||
          existingSwatch.classList.contains(
            'product-page__color-swatch--unavailable'
          ),
        isProductPage: existingSwatch.classList.contains(
          'product-page__color-swatch'
        ),
      };

      swatches.push(swatchData);
    });

    return swatches;
  }

  async findSwatchesViaSearchAPI(
    baseTitle,
    currentProductId,
    existingProductIds
  ) {
    const swatches = [];
    const foundProductIds = new Set([
      currentProductId,
      ...(existingProductIds || []),
    ]);

    try {
      // Get storefront access token from global window object
      const accessToken = window.storefrontAccessToken;
      if (!accessToken) {
        console.debug(
          'Storefront Access Token is missing. Please add it in theme settings.'
        );
        return swatches;
      }

      // Get shop URL
      const shopUrl = window.shopUrl || window.Shopify?.shop || '';
      if (!shopUrl) {
        console.debug('Shop URL is missing.');
        return swatches;
      }

      // Extract domain from shop URL (handle both full URLs and domains)
      const shopDomain = shopUrl.includes('://')
        ? new URL(shopUrl).hostname
        : shopUrl.replace(/^https?:\/\//, '').split('/')[0];

      // Use GraphQL Storefront API to search for products
      const graphqlQuery = `query searchProducts($query: String!, $first: Int) {
        search(query: $query, first: $first, types: PRODUCT) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                availableForSale
                onlineStoreUrl
                featuredImage {
                  id
                  src
                }
                color_swatch: metafield(namespace: "custom", key: "color_swatch") {
                  value
                  type
                }
              }
            }
          }
        }
      }`;

      // Use relative path to avoid CORS issues
      const graphqlUrl = `/api/2025-04/graphql.json`;

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: {
            query: baseTitle,
            first: 200,
          },
        }),
      });

      if (!response.ok) {
        console.debug('GraphQL request failed:', response.status);
        return swatches;
      }

      const result = await response.json();

      if (result.errors) {
        console.debug('GraphQL Errors:', result.errors);
        return swatches;
      }

      // Filter products that match the base title exactly and are published to Online Store
      // onlineStoreUrl is null if product is NOT published to Online Store channel
      // Only include products where onlineStoreUrl is NOT null
      const products =
        result.data?.search?.edges
          ?.map((edge) => edge.node)
         .filter((product) => {
  if (!product.title) return false;

  // Ensure product is published to Online Store
  if (!product.onlineStoreUrl || product.onlineStoreUrl === '') return false;

  // Extract base title before " - Color"
  const productBase = product.title.split(' - ')[0]?.trim();
  const currentBase = baseTitle?.trim();

  return productBase === currentBase;
})|| [];

      for (const product of products) {
        if (swatches.length >= this.maxSwatches) {
          break;
        }

        // Extract product ID (remove 'gid://shopify/Product/' prefix)
        const productId = product.id.split('/').pop();

        // Skip if already found or is current product
        if (
          !productId ||
          foundProductIds.has(productId) ||
          productId === currentProductId
        ) {
          continue;
        }

        // Extract color name from title (everything after ' - ')
        const colorName = product.title.split(' - ').pop()?.trim() || '';
        const colorSwatch = product.color_swatch;

        // Skip if no color swatch metafield
        if (!colorSwatch || !colorSwatch.value) {
          continue;
        }

        foundProductIds.add(productId);

        // Build product URL
        const productUrl = `/products/${product.handle}`;

        const swatchData = {
          color: colorName ? colorName.toLowerCase().replace(/\s+/g, '-') : '',
          productId: productId,
          productHandle: product.handle,
          productUrl: productUrl,
          backgroundColor: colorSwatch.value,
          isUnavailable: !product.availableForSale,
          isProductPage: false,
        };

        swatches.push(swatchData);
      }
    } catch (error) {
      console.debug('Error loading swatches via GraphQL API:', error);
    }

    return swatches;
  }

  renderSwatches(container, swatches) {
    const isProductPage = container.classList.contains(
      'product-page__color-swatches'
    );
    const wrapper = isProductPage
      ? container
      : container.querySelector('.swiper-wrapper');

    if (!wrapper) {
      return;
    }

    swatches.forEach((swatch, index) => {
      const swatchClass = isProductPage
        ? 'product-page__color-swatch product-page__color-swatch--fade-in'
        : 'product-card__color-swatch product-card__color-swatch--fade-in';

      const unavailableClass = isProductPage
        ? 'product-page__color-swatch--unavailable'
        : 'product-card__color-swatch--unavailable';

      const labelClass = isProductPage
        ? 'product-page__color-swatch-label'
        : 'product-card__color-swatch-label';

      const swatchDiv = document.createElement('div');
      swatchDiv.className = `${swatchClass}${
        swatch.isUnavailable ? ` ${unavailableClass}` : ''
      }`;
      swatchDiv.dataset.color = swatch.color;
      swatchDiv.dataset.productId = swatch.productId;
      swatchDiv.dataset.productHandle = swatch.productHandle;
      swatchDiv.dataset.productUrl = swatch.productUrl;

      // Set size based on type
      if (isProductPage) {
        swatchDiv.style.cssText = `background-color: ${swatch.backgroundColor}; width: 2.5rem; height: 2.5rem; cursor: pointer;`;
      } else {
        swatchDiv.style.cssText = `background-color: ${swatch.backgroundColor}; min-width: 2rem; min-height: 2rem; cursor: pointer;`;
      }

      const label = document.createElement('span');
      label.className = labelClass;
      swatchDiv.appendChild(label);

      // Product cards use swiper slides, product pages don't
      if (isProductPage) {
        wrapper.appendChild(swatchDiv);
      } else {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(swatchDiv);
        wrapper.appendChild(slide);
      }

      // Stagger the fade-in animation for each swatch
      setTimeout(() => {
        const fadeInActiveClass = isProductPage
          ? 'product-page__color-swatch--fade-in-active'
          : 'product-card__color-swatch--fade-in-active';
        swatchDiv.classList.add(fadeInActiveClass);
      }, index * 50); // 50ms delay between each swatch
    });

    // Reinitialize Swiper only for product cards (product pages don't use Swiper)
    if (!isProductPage && typeof Swiper !== 'undefined') {
      if (container.swiper) {
        container.swiper.update();
      } else {
        // Initialize Swiper if not already initialized
        const swiperOptions = JSON.parse(
          container.dataset.swiperOptions || '{}'
        );
        new Swiper(container, {
          freeMode: true,
          slidesPerView: 'auto',
          spaceBetween: 8,
          enabled: true,
          ...swiperOptions,
        });
      }
    }
  }
}

// Initialize when DOM is ready
try {
  new ProductCardSwatchLoader();
} catch (error) {
  console.error('Error initializing ProductCardSwatchLoader:', error);
}
